import { DefaultRandom, type Random } from '../../core/Random';
import { cellKey, type HoldAndSpinModel } from '../../model/slot/HoldAndSpinModel';
import type { MoneyCell } from '../../model/slot/HoldAndSpinEvents';
import type { SlotMachine } from '../../widgets/slot/SlotMachine';
import {
  WeightedMoneyValueProvider,
  type MoneyValueProvider,
} from '../../widgets/slot/MoneyValueProvider';
import type { SlotSymbolDef, SpinOutcome } from '../../widgets/slot/types';
import type { Logic } from '../Logic';

/**
 * Default trigger threshold — N money symbols on the trigger spin starts
 * the bonus. cs-core's MMS-prod uses 6; Lightning Link variants use 5+;
 * we use 3 for the demo so a normal play session sees the bonus within
 * ~10 spins.
 *
 * <p>Tuning note (producer audit Phase 4) — bumped down from 4 → 3
 * because the playtest "doesn't seem to work" feedback was real: at
 * forest's money weight 1.5 (total weights 22.5 → 6.67% per cell on a
 * 15-cell grid), the binomial probability of ≥4 money symbols is
 * ~1.5% per spin (once every ~65 spins). At threshold 3 the rate is
 * ~9.3% (once every ~11 spins) — visible in casual play without
 * feeling cheap. Production tuning lifts this back to 6 paired with
 * a lower money-symbol weight and a real economy model.
 */
export const DEFAULT_HOLD_AND_SPIN_TRIGGER = 3;

/** Default initial respins on bonus start AND on every "new symbol landed"
 * reset. Industry-standard MMS hold-and-spin uses 3. */
export const DEFAULT_HOLD_AND_SPIN_RESPINS = 3;

/**
 * How much to multiply the money symbol's base weight by during respins.
 * The base spin uses raw weights (~3% chance per cell at weight 0.6 vs
 * total ~22); during respins this bumps it ~5× so respins land more
 * money symbols (the "feels worth chasing" pacing).
 */
export const DEFAULT_BONUS_MONEY_WEIGHT_BOOST = 5;

export interface HoldAndSpinLogicConfig {
  /** Money-weight multiplier for bonus respins.
   * {@link DEFAULT_BONUS_MONEY_WEIGHT_BOOST}. */
  bonusMoneyWeightBoost?: number;
  /** Random for value-pool draws. Falls back to {@link DefaultRandom}. */
  random?: Random;
  /** Pluggable value-pool — defaults to {@link WeightedMoneyValueProvider}
   * with {@link DEFAULT_MONEY_VALUE_POOL}. */
  valueProvider?: MoneyValueProvider;
  /**
   * Hook invoked after {@link HoldAndSpinModel.start} but before any
   * respin runs — host scenes use it to show the trigger fanfare modal
   * and let it dismiss before the respin loop begins.
   */
  onBonusStarted?: (model: HoldAndSpinModel) => Promise<void>;
  /**
   * Hook invoked after each respin lands and the model has recorded
   * any new cells / consumed a respin — gives the host scene a chance
   * to play per-respin SFX / wait for animations.
   */
  onRespinLanded?: (
    model: HoldAndSpinModel,
    landedThisRespin: readonly MoneyCell[],
  ) => Promise<void>;
}

export interface HoldAndSpinInput {
  /** Caller-supplied bonus model — already constructed with the right
   * grid dims / initial respins. The Logic mutates it through its
   * lifecycle (start → record/consume → complete), and the caller's
   * subscribers see every event including the trigger {@code start}
   * event. (If we constructed the model inline, subscribers attached
   * after the call would miss the start event.) */
  model: HoldAndSpinModel;
  /** The trigger spin's full outcome — money cells on this grid become
   * the initial locked cells. */
  triggerOutcome: SpinOutcome;
  /** Bet that triggered the bonus — every value-pool draw is multiplied
   * by this. */
  bet: number;
}

export interface HoldAndSpinResult {
  /** Sum of every locked cell's coin value. */
  totalWinnings: number;
  /** Number of money cells locked at the end (1× per (col,row)). */
  totalLocked: number;
  /** True if the grid filled completely — fanfare hook for the host scene. */
  fullGrid: boolean;
}

/**
 * Orchestrates one full MMS-style hold-and-spin bonus run:
 *
 * <ol>
 *   <li>Detect money cells on the trigger outcome, assign each a coin
 *       value via {@link MoneyValueProvider}.
 *   <li>Start the {@link HoldAndSpinModel} (events fire: scene picks them
 *       up to render the lock-in animation + respin counter).
 *   <li>Run respins: {@link SlotMachine.respinUnlocked} with locked cells
 *       pinned. After each respin, find new money cells; if any landed,
 *       value-and-record (resets respin counter). Otherwise consume one
 *       respin.
 *   <li>Loop until {@code respinsRemaining === 0} or {@code isGridFull()}.
 *   <li>Compute payout, complete model, return result.
 * </ol>
 *
 * <p>Stateful between runs: each invocation builds a fresh
 * {@link HoldAndSpinModel} so multiple bonuses in one session don't
 * leak state. The host scene listens to that model's events for visuals;
 * the logic's job is the math + state machine.
 */
export class HoldAndSpinLogic
  implements Logic<HoldAndSpinInput, HoldAndSpinResult>
{
  private readonly random: Random;
  private readonly valueProvider: MoneyValueProvider;
  private readonly bonusMoneyWeightBoost: number;
  /** Optional hooks for the scene to inject animation pacing. */
  private readonly onBonusStarted?: HoldAndSpinLogicConfig['onBonusStarted'];
  private readonly onRespinLanded?: HoldAndSpinLogicConfig['onRespinLanded'];

  constructor(
    private readonly machine: SlotMachine,
    private readonly baseSymbols: readonly SlotSymbolDef[],
    config: HoldAndSpinLogicConfig = {},
  ) {
    this.random = config.random ?? new DefaultRandom();
    this.valueProvider = config.valueProvider ?? new WeightedMoneyValueProvider();
    this.bonusMoneyWeightBoost =
      config.bonusMoneyWeightBoost ?? DEFAULT_BONUS_MONEY_WEIGHT_BOOST;
    this.onBonusStarted = config.onBonusStarted;
    this.onRespinLanded = config.onRespinLanded;
  }

  async execute(input: HoldAndSpinInput): Promise<HoldAndSpinResult> {
    const triggerCells = detectMoneyCells(input.triggerOutcome);
    if (triggerCells.length === 0) {
      // Defensive — host should only call us when trigger threshold is met.
      return { totalWinnings: 0, totalLocked: 0, fullGrid: false };
    }

    const model = input.model;
    const bonusSymbols = bumpMoneyWeight(
      this.baseSymbols,
      this.bonusMoneyWeightBoost,
    );

    // The "money def" needed to pin locked cells back to their original
    // appearance during respins. Find it once from the base roster.
    const moneyDef = this.baseSymbols.find((s) => s.isMoney) ?? null;

    // Assign values to trigger-spin money cells + start bonus.
    const valuedTrigger = triggerCells.map(({ col, row }) => {
      const value = this.valueProvider.pickValue(input.bet, this.random);
      return {
        col,
        row,
        value,
        betMultiplier: input.bet > 0 ? value / input.bet : 1,
      };
    });
    model.start(valuedTrigger, input.bet);

    if (this.onBonusStarted) {
      await this.onBonusStarted(model);
    }

    // Build the locked-defs map for the machine. Money def per cell is
    // whatever def was on the trigger outcome at that position.
    const lockedDefs = new Map<string, SlotSymbolDef>();
    for (const cell of triggerCells) {
      const def = input.triggerOutcome.grid[cell.col]?.[cell.row];
      if (def) {
        lockedDefs.set(cellKey(cell.col, cell.row), def);
      }
    }
    // Fallback: if for any reason a triggerCell didn't resolve, use the
    // shared moneyDef so the cell still renders.
    if (moneyDef) {
      for (const cell of triggerCells) {
        const k = cellKey(cell.col, cell.row);
        if (!lockedDefs.has(k)) lockedDefs.set(k, moneyDef);
      }
    }

    // Respin loop.
    while (
      model.respinsRemaining > 0 &&
      !model.isGridFull()
    ) {
      const respinOutcome = await this.machine.respinUnlocked({
        lockedDefs,
        bonusSymbols,
      });

      // Find newly-landed money cells (excluding ones already locked).
      const newCells = detectMoneyCells(respinOutcome).filter(
        (c) => !model.hasCellLocked(c.col, c.row),
      );

      let landedThisRespin: readonly MoneyCell[] = [];
      if (newCells.length > 0) {
        const valued = newCells.map((c) => {
          const value = this.valueProvider.pickValue(input.bet, this.random);
          return {
            ...c,
            value,
            betMultiplier: input.bet > 0 ? value / input.bet : 1,
          };
        });
        model.recordLandedCells(valued);
        // Pull the just-landed cells back from the model so the round
        // stamp matches what subscribers saw on the event.
        landedThisRespin = model
          .cellsList()
          .filter((c) => c.roundLanded === model.round);
        // Sync lockedDefs so subsequent respins keep them pinned.
        for (const cell of newCells) {
          const def = respinOutcome.grid[cell.col]?.[cell.row];
          if (def) lockedDefs.set(cellKey(cell.col, cell.row), def);
          else if (moneyDef) lockedDefs.set(cellKey(cell.col, cell.row), moneyDef);
        }
      } else {
        model.consumeRespin();
      }

      if (this.onRespinLanded) {
        await this.onRespinLanded(model, landedThisRespin);
      }
    }

    const result: HoldAndSpinResult = {
      totalWinnings: model.totalWinnings(),
      totalLocked: model.totalLocked,
      fullGrid: model.isGridFull(),
    };
    model.complete();
    return result;
  }
}

/** Walk the grid and return every money-symbol position. */
export function detectMoneyCells(
  outcome: SpinOutcome,
): readonly { col: number; row: number }[] {
  const cells: { col: number; row: number }[] = [];
  for (let col = 0; col < outcome.grid.length; col++) {
    const reel = outcome.grid[col]!;
    for (let row = 0; row < reel.length; row++) {
      if (reel[row]!.isMoney) cells.push({ col, row });
    }
  }
  return cells;
}

/** Walk the grid and return every {@code 'bonus'}-id position. The
 * multiplayer Reel Race's server-side spin handler drops these into
 * the grid in lieu of a regular forest item; the slot scene
 * celebrates them unconditionally to play the bonus symbol's
 * win-celebration video. */
export function detectBonusCells(
  outcome: SpinOutcome,
): readonly { col: number; row: number }[] {
  const cells: { col: number; row: number }[] = [];
  for (let col = 0; col < outcome.grid.length; col++) {
    const reel = outcome.grid[col]!;
    for (let row = 0; row < reel.length; row++) {
      if (reel[row]!.id === 'bonus') cells.push({ col, row });
    }
  }
  return cells;
}

/** Walk the grid and return every {@code 'item_drop'}-id position.
 * Server-side {@code WheelGameRules.handleReelRaceSpin} writes this
 * symbol at the deterministic drop cell when a loadout item awards
 * on the spin — the visual analogue of how {@code 'bonus'} marks
 * coin-drop cells. The host scene reads the position to drive the
 * cycle-and-settle reveal (see
 * {@link import('../../widgets/slot/SlotMachine').SlotMachine#playItemDropReveal}). */
export function detectItemDropCells(
  outcome: SpinOutcome,
): readonly { col: number; row: number }[] {
  const cells: { col: number; row: number }[] = [];
  for (let col = 0; col < outcome.grid.length; col++) {
    const reel = outcome.grid[col]!;
    for (let row = 0; row < reel.length; row++) {
      if (reel[row]!.id === 'item_drop') cells.push({ col, row });
    }
  }
  return cells;
}

/** Return a copy of the symbol roster with the money symbol's weight
 * multiplied by {@code factor}. Other symbols pass through unchanged. */
export function bumpMoneyWeight(
  roster: readonly SlotSymbolDef[],
  factor: number,
): readonly SlotSymbolDef[] {
  return roster.map((s) =>
    s.isMoney ? { ...s, weight: s.weight * factor } : s,
  );
}
